import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../../Components/Ui/Card';
import { TrendingUp, TrendingDown, Package, DollarSign, Calendar, Loader } from 'lucide-react';
import Button from '../../../Components/Ui/Button';
import { dummyTopProducts } from '../../../Data/Dummydata';
import { useMyBrandInfoQuery } from '../../../app/services/authApi';
import { addBrand } from '../../../app/reducers/myBrand/slice';
const DashboardAnalytics = ({ salesData, topProducts = [], totalOrders = 0, totalRevenue = 0 }) => {
  const { _id, userId } = useSelector((state) => state.userInfo.user);
  const dispatch = useDispatch();
  const { data = {}, isLoading } = useMyBrandInfoQuery(userId || _id);
  useEffect(() => {
    if (!isLoading) {
      const { data: brandData = {} } = data;
      dispatch(addBrand(brandData));
    }
  }, [data, dispatch, isLoading]);
  const [dateRange, setDateRange] = useState('month');
  const calculateGrowth = (current, previous) => {
    if (previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  const previousPeriodRevenue = totalRevenue * 0.85;
  const revenueGrowth = calculateGrowth(totalRevenue, previousPeriodRevenue);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };
  if (isLoading) {
    return <Loader />;
  }
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 md:items-start   justify-between">
        <h1 className="text-lg xl:text-2xl font-bold px-4 md:px-5 text-center md:text-left ">
          Dashboard
        </h1>

        <div className="flex flex-wrap justify-center items-center gap-3 px-4 md:px-0">
          <Button
            className="focus:outline-none focus:ring-2 rounded-lg border-color focus:ring-offset-0"
            variant={dateRange === 'week' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateRange('week')}
          >
            This Week
          </Button>
          <Button
            className="focus:outline-none focus:ring-2 rounded-lg border-color focus:ring-offset-0"
            variant={dateRange === 'month' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateRange('month')}
          >
            This Month
          </Button>
          <Button
            className="focus:outline-none focus:ring-2 rounded-lg border-color focus:ring-offset-0"
            variant={dateRange === 'year' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateRange('year')}
          >
            This Year
          </Button>
          <Button
            className="focus:outline-none focus:ring-2 rounded-lg border-color focus:ring-offset-0"
            variant="outline"
            size="sm"
            leftIcon={<Calendar size={16} />}
            onClick={() => {
              // Open custom date picker
            }}
          >
            Custom
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full px-4 md:px-0">
        {/* Total Revenue Card */}
        <Card className="bg-gradient-to-br from-primary/10 to-white border border-primary/10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-secondary">Total Revenue</h3>
              <p className="mt-1 xl:text-2xl font-semibold text-gray-900">
                {formatCurrency(totalRevenue)}
              </p>
              <div className="mt-1 flex items-center">
                {revenueGrowth >= 0 ? (
                  <TrendingUp size={16} className="text-green-500 mr-1" />
                ) : (
                  <TrendingDown size={16} className="text-red-500 mr-1" />
                )}
                <span className={revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {Math.abs(revenueGrowth).toFixed(1)}%
                </span>
                <span className="text-gray-500 text-xs ml-1">vs previous period</span>
              </div>
            </div>
            <div className="h-12 w-12 bg-primary-medium rounded-full flex items-center justify-center">
              <DollarSign size={24} className="text-primary" />
            </div>
          </div>
        </Card>

        {/* Top Products Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-white border border-amber-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-secondary">Top Products</h3>
              <p className="mt-1 xl:text-2xl font-semibold text-gray-900">
                {topProducts?.length ?? 0}
              </p>
              <div className="mt-1 flex items-center">
                <span className="text-gray-500 text-sm">Active products</span>
              </div>
            </div>
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Package size={24} className="text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 w-full px-4 md:px-0">
        <Card className="xl:col-span-2">
          <div className="flex justify-between items-center mb-6">
            <h2 className="xl:text-lg text-sm text-nowrap xl:font-semibold text-gray-900 px-2">
              Sales Overview
            </h2>
            <div className="flex items-center text-sm text-gray-500">
              <span className="flex items-center mr-4">
                <span
                  className="w-3 h-3 rounded-full bg-purple-500 mr-1"
                  style={{ backgroundColor: '#832474' }}
                ></span>
                Sales
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-blue-300 mr-1"></span>
                Orders
              </span>
            </div>
          </div>

          <div className="w-full h-80 overflow-x-auto">
            <div className="w-full h-full relative min-w-[600px]">
              {/* Grid background */}
              <div className="absolute inset-0 grid grid-cols-6 grid-rows-4">
                {Array.from({ length: 24 }).map((_, index) => (
                  <div key={index} className="border-t border-l border-gray-100"></div>
                ))}
              </div>

              {/* Y-Axis */}
              <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-2 text-xs text-gray-500 px-2">
                <div>50k</div>
                <div>40k</div>
                <div>30k</div>
                <div>20k</div>
                <div>10k</div>
                <div>0</div>
              </div>

              {/* X-Axis */}
              <div className="absolute bottom-2 left-10 right-0 flex justify-between text-[10px] md:text-xs text-gray-500">
                <div>Jan</div>
                <div>Feb</div>
                <div>Mar</div>
                <div>Apr</div>
                <div>May</div>
                <div>Jun</div>
                <div>Jul</div>
              </div>

              {/* Purple Bars */}
              <div className="absolute bottom-8 left-12 right-6 flex justify-between items-end h-64">
                {[35, 42, 38, 50, 35, 55, 60].map((height, index) => (
                  <div
                    key={index}
                    className="w-4 md:w-6 bg-purple-500 rounded-t-sm mx-1 md:mx-2"
                    style={{
                      height: `${height}%`,
                      backgroundColor: '#832474',
                    }}
                  ></div>
                ))}
              </div>

              {/* Orders Line */}
              <svg
                className="absolute bottom-8 left-12 right-6 h-64"
                style={{ overflow: 'visible' }}
              >
                <polyline
                  points="15,115 63,90 111,100 159,70 207,95 255,65 303,55"
                  fill="none"
                  stroke="#93c5fd"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {[115, 90, 100, 70, 95, 65, 55].map((point, index) => (
                  <circle
                    key={index}
                    cx={15 + index * 48}
                    cy={point}
                    r="4"
                    fill="white"
                    stroke="#93c5fd"
                    strokeWidth="2"
                  />
                ))}
              </svg>
            </div>
          </div>
        </Card>

        <Card className="text-sm xl:text-base" title="Top Products">
          <div className="space-y-3">
            {dummyTopProducts.slice(0, 5).map((product) => (
              <div
                key={product.id}
                className="flex items-center justify-between pb-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-500">
                    ₹{product.finalPrice ? product.finalPrice.toFixed(2) : '0.00'}
                  </p>
                </div>
                <span className="text-primary font-medium">{product.sold} sold</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAnalytics;
